/**
 * Run-agent mutation. The returned ``AgentTurnResult`` is mutation
 * output (analysis + letter html + pending request); the caller pushes
 * it into the Zustand store. Invalidate the session query afterwards
 * so the agent's updated ``status`` field is refetched.
 */

import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useRunAgentApiSessionsSessionIdRunPost } from '@/codegen/openapi/client';
import { QUERY_KEY } from '@/config/constants';
import { deferredInvalidateQueries } from './utils/deferredInvalidation';
import type { AgentTurnResult } from '@/api/types';

export function useRunAgent() {
    const queryClient = useQueryClient();
    const { mutateAsync, isPending } = useRunAgentApiSessionsSessionIdRunPost();

    const runAgent = useCallback(
        async (sessionId: string): Promise<AgentTurnResult> => {
            const r = await mutateAsync({ sessionId });
            if (!r.data) throw new Error(r.error?.toString() ?? 'Agent failed');
            deferredInvalidateQueries(queryClient, [
                [QUERY_KEY.SESSION, sessionId],
                [QUERY_KEY.PROGRESS, sessionId],
            ]);
            return r.data;
        },
        [mutateAsync, queryClient],
    );

    return useMemo(() => ({ runAgent, isPending }), [runAgent, isPending]);
}
